import { useState, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { format } from 'date-fns'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useEventTypes } from '@/hooks/use-event-types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { ReportDatePicker } from '@/components/reporting/report-date-picker'
import { organizationStore } from '@/stores/organization.store'
import {
  useReportingMetrics,
  formatCents,
  formatCentsDetailed,
  getDateRangeForPreset,
  getCustomDateRange,
  DATE_RANGE_PRESET_LABELS,
  DATE_RANGE_PRESETS,
  type DateRangePreset,
} from '@/stores/reporting.store'

export const Route = createFileRoute('/_app/_home/revenue')({
  component: RevenuePage,
})

// ── Metric Card ──

function MetricCard({
  label,
  value,
  subtitle,
  isLoading,
  accentColor,
}: {
  label: string
  value: string | number
  subtitle?: string
  isLoading?: boolean
  accentColor?: string
}) {
  return (
    <Card>
      <CardContent className="p-5">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-8 w-24 mb-1" />
            <Skeleton className="h-3 w-16" />
          </>
        ) : (
          <>
            <div className="font-financial text-xs uppercase tracking-[0.2em] text-dim mb-1">
              {label}
            </div>
            <div className={`font-financial text-2xl font-bold ${accentColor || ''}`}>{value}</div>
            {subtitle && (
              <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ── Revenue Bar Chart (CSS-only, no deps) ──

function RevenueBarChart({
  data,
  isLoading,
}: {
  data: { label: string; collected: number; invoiced: number }[]
  isLoading: boolean
}) {
  const maxValue = useMemo(() => {
    if (!data.length) return 1
    return Math.max(...data.map((d) => Math.max(d.collected, d.invoiced)), 1)
  }, [data])

  if (isLoading) {
    return (
      <div className="flex items-end gap-2 h-48">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <Skeleton className="w-full" style={{ height: `${30 + Math.random() * 70}%` }} />
            <Skeleton className="h-3 w-8" />
          </div>
        ))}
      </div>
    )
  }

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
        No revenue data for this period
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1.5 h-48">
        {data.map((d, i) => {
          const collectedHeight = (d.collected / maxValue) * 100
          const invoicedHeight = (d.invoiced / maxValue) * 100

          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end group relative">
              {/* Tooltip on hover */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full bg-popover border rounded-md px-2 py-1 text-xs shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                <div className="font-medium">{d.label}</div>
                <div className="font-financial text-chart-2">Collected: {formatCents(d.collected)}</div>
                <div className="font-financial text-chart-3">Invoiced: {formatCents(d.invoiced)}</div>
              </div>
              {/* Bars */}
              <div className="w-full flex gap-0.5 items-end h-full">
                <div
                  className="flex-1 bg-chart-2/80 rounded-t transition-all duration-300 min-h-[2px]"
                  style={{ height: `${Math.max(collectedHeight, 1)}%` }}
                />
                <div
                  className="flex-1 bg-chart-3/60 rounded-t transition-all duration-300 min-h-[2px]"
                  style={{ height: `${Math.max(invoicedHeight, 1)}%` }}
                />
              </div>
              <div className="text-[10px] text-muted-foreground truncate w-full text-center">
                {d.label.split(' ')[0]}
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex items-center gap-4 justify-center text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-chart-2/80" />
          Collected
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-chart-3/60" />
          Invoiced
        </div>
      </div>
    </div>
  )
}

// ── Conversion Funnel ──

function ConversionFunnel({
  totalLeads,
  convertedLeads,
  closeRate,
  countByStage,
  isLoading,
}: {
  totalLeads: number
  convertedLeads: number
  closeRate: number
  countByStage: Record<string, number>
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    )
  }

  // Map stage names to readable labels
  const stageLabels: Record<string, string> = {
    New: 'New Leads',
    'In Progress': 'In Progress',
    'Quote Sent': 'Quote Sent',
    Accepted: 'Accepted',
    Declined: 'Declined',
    Cancelled: 'Cancelled',
  }

  const stageColors: Record<string, string> = {
    New: 'bg-chart-3',
    'In Progress': 'bg-chart-1',
    'Quote Sent': 'bg-chart-4',
    Accepted: 'bg-chart-2',
    Declined: 'bg-chart-5',
    Cancelled: 'bg-status-neutral-fg',
  }

  // Order stages in funnel order
  const stageOrder = ['New', 'In Progress', 'Quote Sent', 'Accepted', 'Declined', 'Cancelled']
  const sortedStages = stageOrder
    .filter((stage) => (countByStage[stage] || 0) > 0)
    .map((stage) => ({
      stage,
      label: stageLabels[stage] || stage,
      count: countByStage[stage] || 0,
      color: stageColors[stage] || 'bg-status-neutral-fg',
    }))

  return (
    <div className="space-y-4">
      {/* Close rate highlight */}
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
        <span className="text-sm font-medium">Close Rate</span>
        <div className="flex items-center gap-2">
          <span className="font-financial text-2xl font-bold text-status-success-fg">
            {closeRate.toFixed(1)}%
          </span>
          <span className="text-xs text-muted-foreground">
            ({convertedLeads}/{totalLeads})
          </span>
        </div>
      </div>

      {/* Stage breakdown */}
      <div className="space-y-2">
        {sortedStages.map(({ stage, label, count, color }) => {
          const width = totalLeads > 0 ? (count / totalLeads) * 100 : 0
          return (
            <div key={stage} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-financial font-medium">{count}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${color} rounded-full transition-all duration-500`}
                  style={{ width: `${Math.max(width, 2)}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Revenue By Event Type ──

function EventTypeBreakdown({
  data,
  isLoading,
}: {
  data: { eventTypeKey: string; eventTypeLabel: string; revenueCents: number; eventCount: number }[]
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        No event data for this period
      </div>
    )
  }

  const maxRevenue = Math.max(...data.map((d) => d.revenueCents), 1)

  const colors = [
    'bg-chart-2',
    'bg-chart-3',
    'bg-chart-4',
    'bg-chart-1',
    'bg-chart-5',
    'bg-chart-3',
    'bg-chart-4',
    'bg-chart-2',
  ]

  return (
    <div className="space-y-3">
      {data.map((item, i) => {
        const width = (item.revenueCents / maxRevenue) * 100
        return (
          <div key={item.eventTypeKey} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{item.eventTypeLabel}</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {item.eventCount} event{item.eventCount !== 1 ? 's' : ''}
                </Badge>
              </div>
              <span className="font-financial text-sm font-bold">{formatCents(item.revenueCents)}</span>
            </div>
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${colors[i % colors.length]} rounded-full transition-all duration-500`}
                style={{ width: `${Math.max(width, 3)}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main Revenue Page ──

function RevenuePage() {
  useBreadcrumbFromRoute()

  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  const { getEventTypeLabel, isLoading: eventTypesLoading } = useEventTypes()
  const [preset, setPreset] = useState<DateRangePreset>('this_year')
  const [isCustom, setIsCustom] = useState(false)
  const [customStart, setCustomStart] = useState<Date | undefined>(undefined)
  const [customEnd, setCustomEnd] = useState<Date | undefined>(undefined)

  // Until both custom days are picked, keep showing the last preset's range.
  const { startDate, endDate } = useMemo(() => {
    if (isCustom && customStart && customEnd) {
      return customStart <= customEnd
        ? getCustomDateRange(customStart, customEnd)
        : getCustomDateRange(customEnd, customStart)
    }
    return getDateRangeForPreset(preset)
  }, [isCustom, preset, customStart, customEnd])

  // The query end is exclusive; show the last included day to the user.
  const inclusiveEndDate = useMemo(() => {
    const d = new Date(endDate)
    d.setDate(d.getDate() - 1)
    return d
  }, [endDate])

  const {
    closeRate,
    leadsCount,
    revenuePipeline,
    monthlyRevenue,
    revenueByEventType,
    isLoading,
    hasLoaded,
  } = useReportingMetrics(selectedOrganizationId || '', startDate, endDate)

  const loading = isLoading || !hasLoaded || eventTypesLoading

  // Transform monthly revenue for chart
  const chartData = useMemo(() => {
    if (!monthlyRevenue) return []
    return monthlyRevenue.map((m) => ({
      label: m.label,
      collected: Number(m.collectedCents),
      invoiced: Number(m.invoicedCents),
    }))
  }, [monthlyRevenue])

  // Transform event type data
  const eventTypeData = useMemo(() => {
    if (!revenueByEventType) return []
    return revenueByEventType.map((r) => ({
      eventTypeKey: r.eventType,
      eventTypeLabel: getEventTypeLabel(r.eventType),
      revenueCents: Number(r.revenueCents),
      eventCount: r.eventCount,
    }))
  }, [getEventTypeLabel, revenueByEventType])

  return (
    <div className="flex h-full flex-col gap-6 p-6 overflow-y-auto">
      {/* Header with period selector */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="display-heading text-2xl font-semibold tracking-tight">Revenue Intelligence</h1>
          <div className="flex flex-wrap items-center gap-1 bg-muted rounded-lg p-1">
            {DATE_RANGE_PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => {
                  setPreset(p)
                  setIsCustom(false)
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  !isCustom && preset === p
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {DATE_RANGE_PRESET_LABELS[p]}
              </button>
            ))}
            <button
              onClick={() => setIsCustom(true)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                isCustom
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Custom
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Period:</span>
          {isCustom && (
            <div className="flex items-center gap-2">
              <ReportDatePicker date={customStart} onSelect={setCustomStart} placeholder="Start date" />
              <span>to</span>
              <ReportDatePicker date={customEnd} onSelect={setCustomEnd} placeholder="End date" />
            </div>
          )}
          {(!isCustom || (customStart && customEnd)) && (
            <span className="font-financial">
              {format(startDate, 'MMM d, yyyy')} &mdash; {format(inclusiveEndDate, 'MMM d, yyyy')}
            </span>
          )}
        </div>
      </div>

      {/* Revenue Pipeline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <MetricCard
          label="Collected"
          value={revenuePipeline ? formatCentsDetailed(revenuePipeline.collectedRevenueCents) : '$0'}
          subtitle="All payments received"
          isLoading={loading}
          accentColor="text-status-success-fg"
        />
        <MetricCard
          label="Tips Collected"
          value={revenuePipeline ? formatCentsDetailed(revenuePipeline.tipsCollectedCents) : '$0'}
          subtitle="Add to Collected to match Stripe"
          isLoading={loading}
          accentColor="text-status-success-fg"
        />
        <MetricCard
          label="Outstanding"
          value={revenuePipeline ? formatCents(revenuePipeline.outstandingRevenueCents) : '$0'}
          subtitle="Awaiting payment"
          isLoading={loading}
          accentColor="text-status-warning-fg"
        />
        <MetricCard
          label="Total Pipeline"
          value={revenuePipeline ? formatCents(revenuePipeline.totalPipelineCents) : '$0'}
          subtitle={revenuePipeline ? `${revenuePipeline.invoiceCount} invoices` : '0 invoices'}
          isLoading={loading}
        />
        <MetricCard
          label="Avg Invoice"
          value={revenuePipeline ? formatCents(revenuePipeline.averageInvoiceCents) : '$0'}
          subtitle="Per invoice"
          isLoading={loading}
        />
        <MetricCard
          label="Close Rate"
          value={closeRate ? `${closeRate.closeRatePercentage.toFixed(1)}%` : '0%'}
          subtitle={
            closeRate
              ? `${closeRate.convertedLeads} of ${closeRate.totalLeads} leads`
              : '0 leads'
          }
          isLoading={loading}
          accentColor="text-status-info-fg"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Revenue Trend */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenue Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <RevenueBarChart data={chartData} isLoading={loading} />
          </CardContent>
        </Card>

        {/* Revenue by Event Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenue by Event Type</CardTitle>
          </CardHeader>
          <CardContent>
            <EventTypeBreakdown data={eventTypeData} isLoading={loading} />
          </CardContent>
        </Card>
      </div>

      {/* Conversion Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Lead Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <ConversionFunnel
              totalLeads={leadsCount?.totalCount || 0}
              convertedLeads={closeRate?.convertedLeads || 0}
              closeRate={closeRate?.closeRatePercentage || 0}
              countByStage={leadsCount?.countByStage || {}}
              isLoading={loading}
            />
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Period Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <SummaryRow
                  label="Total Leads"
                  value={String(leadsCount?.totalCount || 0)}
                />
                <SummaryRow
                  label="Leads Converted"
                  value={String(closeRate?.convertedLeads || 0)}
                />
                <SummaryRow
                  label="Invoices Created"
                  value={String(revenuePipeline?.invoiceCount || 0)}
                />
                <SummaryRow
                  label="Revenue Collected"
                  value={revenuePipeline ? formatCents(revenuePipeline.collectedRevenueCents) : '$0'}
                  highlight
                />
                <SummaryRow
                  label="Tips Collected"
                  value={revenuePipeline ? formatCentsDetailed(revenuePipeline.tipsCollectedCents) : '$0.00'}
                />
                <SummaryRow
                  label="Collected incl. Tips"
                  value={
                    revenuePipeline
                      ? formatCentsDetailed(
                          revenuePipeline.collectedRevenueCents + revenuePipeline.tipsCollectedCents,
                        )
                      : '$0.00'
                  }
                  highlight
                />
                <SummaryRow
                  label="Revenue Outstanding"
                  value={revenuePipeline ? formatCents(revenuePipeline.outstandingRevenueCents) : '$0'}
                />
                {revenuePipeline && revenuePipeline.invoiceCount > 0 && (
                  <>
                    <div className="border-t pt-3 mt-3" />
                    <SummaryRow
                      label="Collection Rate"
                      value={`${(
                        (Number(revenuePipeline.collectedRevenueCents) /
                          Math.max(Number(revenuePipeline.totalPipelineCents), 1)) *
                        100
                      ).toFixed(1)}%`}
                      highlight
                    />
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function SummaryRow({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`font-financial text-sm ${highlight ? 'font-bold text-foreground' : 'font-medium'}`}>
        {value}
      </span>
    </div>
  )
}
